import { useMemo, useState, useRef, useCallback, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  X,
  BarChart3,
  PieChart,
  MapPin,
  Star,
  Building2,
  Tag,
  TrendingUp,
  Users,
  Globe,
  Calendar,
  Database,
  Layers,
  Download,
  Image,
  FileText,
  ChevronDown,
} from "lucide-react";
import { toPng } from "html-to-image";
import { jsPDF } from "jspdf";
import type { OperatorWithCoords } from "../../types/database";
import { getTypologyConfig, getTypologyLabel, getCategoryConfig, getCategoryLabel } from "../../lib/categoryConfig";
import { cn } from "../../lib/utils";
import { useThemeStore } from "../../stores/themeStore";
import { useAuthStore } from "../../stores/authStore";
import { queryOperatorsProtected } from "../../lib/operatorsApi";
import type { LucideIcon } from "lucide-react";

// Theme colors for export
const exportThemes = {
  dark: {
    bg: "#1a1a1a",
    cardBg: "#262626",
    text: "#ffffff",
    textMuted: "#a1a1aa",
    textSubtle: "#71717a",
    border: "#333",
  },
  light: {
    bg: "#ffffff",
    cardBg: "#f4f4f5",
    text: "#18181b",
    textMuted: "#52525b",
    textSubtle: "#a1a1aa",
    border: "#e4e4e7",
  },
};

interface AnalyticsCardProps {
  allOperators: OperatorWithCoords[];
  filteredOperators: OperatorWithCoords[];
  operatorsInView: OperatorWithCoords[];
  isOpen: boolean;
  onClose: () => void;
}

interface StatItem {
  label: string;
  rawLabel?: string;
  value: number;
  color?: string;
  percentage?: number;
  icon?: LucideIcon;
  // True when the item represents the aggregated "Others" bucket (residual beyond top-N).
  // Lets consumers like top-N cards exclude it without string-matching on the label.
  isOthers?: boolean;
}

// "Others" bucket: sums the residual beyond top-N so the donut center shows the
// real operator count instead of a truncated sum (ex bug: 99,530 vs 100,174 visible).
const OTHERS_LABEL = "Altre";
// Design-system token so "Others" stays legible in light and dark mode without hardcoding.
const OTHERS_COLOR = "hsl(var(--muted-foreground))";

// Appends an "Others" item carrying the residual (total - sum(top)) when > 0.
function appendOthers(top: StatItem[], total: number, label = OTHERS_LABEL): StatItem[] {
  const sumTop = top.reduce((s, i) => s + i.value, 0);
  const residual = total - sumTop;
  if (residual <= 0) return top;
  return [
    ...top,
    {
      label,
      // rawLabel intentionally omitted: the CSV export uses `rawLabel || label`,
      // so we avoid leaking a technical placeholder into the file.
      value: residual,
      color: OTHERS_COLOR,
      percentage: total > 0 ? (residual / total) * 100 : 0,
      isOthers: true,
    },
  ];
}

// Reduces a list to `maxVisible` items, collapsing the tail into a single "Others".
// If the list already contains an isOthers entry (from appendOthers), it gets folded
// into the sum. Used by bars/legends that have fewer slots than the donut.
function takeWithOthers(items: StatItem[], maxVisible: number, label = OTHERS_LABEL): StatItem[] {
  if (items.length <= maxVisible) return items;
  const visible = items.slice(0, maxVisible - 1);
  const rest = items.slice(maxVisible - 1);
  const restValue = rest.reduce((s, i) => s + i.value, 0);
  const restPct = rest.reduce((s, i) => s + (i.percentage ?? 0), 0);
  return [
    ...visible,
    {
      label,
      value: restValue,
      color: OTHERS_COLOR,
      percentage: restPct,
      isOthers: true,
    },
  ];
}

// Compute category breakdown (top 8 + "Others")
function computeCategoryStats(operators: OperatorWithCoords[]): StatItem[] {
  const counts: Record<string, number> = {};
  for (const op of operators) {
    const cat = op.macro_category || "Non specificato";
    counts[cat] = (counts[cat] || 0) + 1;
  }
  const total = operators.length;
  const top = Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8)
    .map(([rawLabel, value]) => {
      const config = getCategoryConfig(rawLabel);
      return {
        label: getCategoryLabel(rawLabel),
        rawLabel,
        value,
        color: config.color,
        icon: config.icon,
        percentage: total > 0 ? (value / total) * 100 : 0,
      };
    });
  return appendOthers(top, total);
}

// Compute typology breakdown (top 10 + "Others")
function computeTypologyStats(operators: OperatorWithCoords[]): StatItem[] {
  const counts: Record<string, number> = {};
  for (const op of operators) {
    const tipo = op.typology || "Non specificato";
    counts[tipo] = (counts[tipo] || 0) + 1;
  }
  const total = operators.length;
  const top = Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10)
    .map(([rawLabel, value]) => {
      const config = getTypologyConfig(rawLabel);
      return {
        label: getTypologyLabel(rawLabel) || rawLabel,
        rawLabel,
        value,
        color: config.color,
        icon: config.icon,
        percentage: total > 0 ? (value / total) * 100 : 0,
      };
    });
  return appendOthers(top, total);
}

// Compute region breakdown (top 10 + "Others")
function computeRegionStats(operators: OperatorWithCoords[]): StatItem[] {
  const counts: Record<string, number> = {};
  for (const op of operators) {
    const reg = op.region || "Non specificato";
    counts[reg] = (counts[reg] || 0) + 1;
  }
  const total = operators.length;
  const top = Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10)
    .map(([label, value]) => ({
      label,
      value,
      percentage: total > 0 ? (value / total) * 100 : 0,
    }));
  return appendOthers(top, total);
}

// Compute star rating distribution
function computeStarsStats(operators: OperatorWithCoords[]): { distribution: StatItem[]; avgStars: number; withStars: number } {
  const starCounts: Record<number, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
  let totalStars = 0;
  let withStars = 0;

  for (const op of operators) {
    if (op.star_rating && op.star_rating > 0 && op.star_rating <= 5) {
      starCounts[Math.round(op.star_rating)] = (starCounts[Math.round(op.star_rating)] || 0) + 1;
      totalStars += op.star_rating;
      withStars++;
    }
  }

  const distribution = [5, 4, 3, 2, 1].map((stars) => ({
    label: `${stars} stelle`,
    value: starCounts[stars] || 0,
    percentage: withStars > 0 ? ((starCounts[stars] || 0) / withStars) * 100 : 0,
  }));

  return {
    distribution,
    avgStars: withStars > 0 ? totalStars / withStars : 0,
    withStars,
  };
}

// Compute events stats (with dates)
function computeEventsStats(operators: OperatorWithCoords[]): { withDates: number; upcoming: number; past: number; active: number } {
  const now = new Date();
  let withDates = 0;
  let upcoming = 0;
  let past = 0;
  let active = 0;

  for (const op of operators) {
    if (op.start_date || op.end_date) {
      withDates++;
      const start = op.start_date ? new Date(op.start_date) : null;
      const end = op.end_date ? new Date(op.end_date) : null;

      if (start && start > now) {
        upcoming++;
      } else if (end && end < now) {
        past++;
      } else if (start && end && start <= now && end >= now) {
        active++;
      } else if (start && !end && start <= now) {
        active++;
      }
    }
  }

  return { withDates, upcoming, past, active };
}

// Compute data source stats (top 6 + "Other sources")
function computeSourceStats(operators: OperatorWithCoords[]): StatItem[] {
  const counts: Record<string, number> = {};
  for (const op of operators) {
    const source = op.source || "Non specificato";
    counts[source] = (counts[source] || 0) + 1;
  }
  const total = operators.length;
  const top = Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 6)
    .map(([label, value]) => ({
      label,
      value,
      percentage: total > 0 ? (value / total) * 100 : 0,
    }));
  return appendOthers(top, total, "Altre fonti");
}

// Tipo per il risultato dell'RPC completeness_stats (conteggi aggregati DB-side)
interface CompletenessResult {
  total: number;
  email: { count: number; pct: number };
  phone: { count: number; pct: number };
  website: { count: number; pct: number };
  photo: { count: number; pct: number };
  description: { count: number; pct: number };
}

// Fallback locale (usato solo se la chiamata server fallisce).
// Nota: i campi email/phone/website/photo/description NON vengono dal search_all
// della mappa (tolti per CPU quota edge-runtime), quindi su dati di mappa locali
// ritornerebbe sempre 0%. I valori reali arrivano dall'RPC operators_completeness.
function computeCompletenessStats(operators: OperatorWithCoords[]): CompletenessResult {
  let withEmail = 0;
  let withPhone = 0;
  let withWebsite = 0;
  let withPhoto = 0;
  let withDescription = 0;

  for (const op of operators) {
    if (op.email) withEmail++;
    if (op.phone) withPhone++;
    if (op.website_url) withWebsite++;
    if (op.photo_url) withPhoto++;
    if (op.description) withDescription++;
  }

  const total = operators.length;
  return {
    total,
    email: { count: withEmail, pct: total > 0 ? (withEmail / total) * 100 : 0 },
    phone: { count: withPhone, pct: total > 0 ? (withPhone / total) * 100 : 0 },
    website: { count: withWebsite, pct: total > 0 ? (withWebsite / total) * 100 : 0 },
    photo: { count: withPhoto, pct: total > 0 ? (withPhoto / total) * 100 : 0 },
    description: { count: withDescription, pct: total > 0 ? (withDescription / total) * 100 : 0 },
  };
}

// ═══════════════════════════════════════════════════════════════
// DONUT CHART - Animated SVG donut for categories
// ═══════════════════════════════════════════════════════════════
function DonutChart({ items, size = 140 }: { items: StatItem[]; size?: number }) {
  const total = items.reduce((sum, i) => sum + i.value, 0);
  const strokeWidth = 24;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;

  let currentOffset = 0;

  return (
    <div className="relative" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="transform -rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="hsl(var(--secondary))"
          strokeWidth={strokeWidth}
        />
        {items.map((item, idx) => {
          const percentage = total > 0 ? item.value / total : 0;
          const segmentLength = circumference * percentage;
          const offset = currentOffset;
          currentOffset += segmentLength;

          // Skip only non-positive slices; the previous threshold (< 0.01) also dropped
          // the "Others" slice when under 1%, leaving an exposed grey background arc.
          if (percentage <= 0) return null;

          return (
            <motion.circle
              key={idx}
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke={item.color || "#284899"}
              strokeWidth={strokeWidth}
              strokeDasharray={`${segmentLength} ${circumference}`}
              strokeDashoffset={-offset}
              strokeLinecap="butt"
              initial={{ strokeDasharray: `0 ${circumference}` }}
              animate={{ strokeDasharray: `${segmentLength} ${circumference}` }}
              transition={{ duration: 0.8, delay: idx * 0.1, ease: "easeOut" }}
            />
          );
        })}
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-2xl font-bold text-foreground tabular-nums">{total.toLocaleString()}</span>
        <span className="text-[12px] text-muted-foreground">operatori</span>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// RADIAL PROGRESS - Circular progress indicator
// ═══════════════════════════════════════════════════════════════
function RadialProgress({
  value,
  max,
  label,
  icon: Icon,
  color = "#284899",
  size = 80
}: {
  value: number;
  max: number;
  label: string;
  icon: LucideIcon;
  color?: string;
  size?: number;
}) {
  const percentage = max > 0 ? (value / max) * 100 : 0;
  const strokeWidth = 6;
  const radius = (size - strokeWidth) / 2 - 4;
  const circumference = 2 * Math.PI * radius;
  const strokeDashoffset = circumference - (percentage / 100) * circumference;

  return (
    <div className="flex flex-col items-center">
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="transform -rotate-90">
          <circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke="hsl(var(--secondary))"
            strokeWidth={strokeWidth}
          />
          <motion.circle
            cx={size / 2}
            cy={size / 2}
            r={radius}
            fill="none"
            stroke={color}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeDasharray={circumference}
            initial={{ strokeDashoffset: circumference }}
            animate={{ strokeDashoffset }}
            transition={{ duration: 1, ease: "easeOut" }}
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center">
          <Icon className="h-5 w-5" style={{ color }} />
        </div>
      </div>
      <div className="mt-2 text-center">
        <p className="text-lg font-bold text-foreground tabular-nums">{value.toLocaleString()}</p>
        <p className="text-[12px] text-muted-foreground leading-tight">{label}</p>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// REGION BARS - Horizontal bars for regional distribution
// ═══════════════════════════════════════════════════════════════
function RegionBars({ items }: { items: StatItem[] }) {
  const total = items.reduce((sum, i) => sum + i.value, 0);
  const colors = [
    "from-blue-500 to-blue-600",
    "from-emerald-500 to-emerald-600",
    "from-[#284899] to-[#1e3570]",
    "from-rose-500 to-rose-600",
    "from-violet-500 to-violet-600",
    "from-cyan-500 to-cyan-600",
  ];

  return (
    <div className="space-y-1.5">
      {items.map((item, idx) => {
        const percentage = total > 0 ? (item.value / total) * 100 : 0;
        const minWidth = Math.max(percentage, 25);
        // "Others" uses its own neutral item.color instead of the idx-based palette.
        const isOthers = item.isOthers;

        return (
          <motion.div
            key={idx}
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: "100%", opacity: 1 }}
            transition={{ duration: 0.4, delay: idx * 0.05 }}
            className="flex items-center gap-2"
          >
            <motion.div
              initial={{ width: 0 }}
              animate={{ width: `${minWidth}%` }}
              transition={{ duration: 0.5, delay: idx * 0.08 }}
              className={cn(
                "h-7 rounded-lg flex items-center justify-between px-2.5 min-w-[100px]",
                !isOthers && "bg-gradient-to-r",
                !isOthers && colors[idx % colors.length]
              )}
              style={isOthers ? { backgroundColor: item.color } : undefined}
            >
              <span className="text-[12px] font-semibold text-white truncate max-w-[70%]">
                {item.label}
              </span>
              <span className="text-[12px] font-bold text-white/90 tabular-nums ml-1">
                {percentage.toFixed(0)}%
              </span>
            </motion.div>
            <span className="text-[12px] text-muted-foreground tabular-nums w-14 text-right">
              {item.value.toLocaleString()}
            </span>
          </motion.div>
        );
      })}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// STAR RATING VISUAL - Radial star distribution
// ═══════════════════════════════════════════════════════════════
function StarRatingVisual({ distribution, avgStars, withStars }: {
  distribution: StatItem[];
  avgStars: number;
  withStars: number;
}) {
  const max = Math.max(...distribution.map(d => d.value), 1);

  return (
    <div className="flex items-center gap-5">
      <div className="relative flex flex-col items-center">
        <div className="relative">
          <motion.div
            initial={{ scale: 0, rotate: -180 }}
            animate={{ scale: 1, rotate: 0 }}
            transition={{ duration: 0.6, type: "spring" }}
            className="w-[72px] h-[72px] rounded-2xl bg-[#284899] flex flex-col items-center justify-center shadow-lg shadow-[#284899]/30"
          >
            <Star className="h-6 w-6 text-white fill-white" />
            <span className="text-lg font-bold text-white tabular-nums mt-0.5">{avgStars.toFixed(1)}</span>
          </motion.div>
        </div>
        <span className="text-[12px] text-muted-foreground mt-2 text-center leading-tight">
          {withStars.toLocaleString()}<br />con rating
        </span>
      </div>

      <div className="flex-1 space-y-1.5">
        {distribution.map((item, idx) => {
          const starCount = 5 - idx;
          const width = max > 0 ? (item.value / max) * 100 : 0;

          return (
            <div key={idx} className="flex items-center gap-2">
              <div className="flex gap-0.5 w-14 justify-end">
                {Array.from({ length: starCount }).map((_, i) => (
                  <Star key={i} className="h-2.5 w-2.5 fill-[#284899] text-[#284899]" />
                ))}
              </div>
              <div className="flex-1 h-4 bg-secondary rounded overflow-hidden">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${width}%` }}
                  transition={{ duration: 0.6, delay: idx * 0.1 }}
                  className="h-full rounded bg-[#284899]"
                />
              </div>
              <span className="text-[12px] font-medium text-foreground w-10 text-right tabular-nums">
                {item.value.toLocaleString()}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// COMPLETENESS GAUGE - Semi-circular gauge chart
// ═══════════════════════════════════════════════════════════════
function CompletenessGauge({ data }: { data: { label: string; percentage: number }[] }) {
  const avgCompleteness = data.reduce((sum, d) => sum + d.percentage, 0) / data.length;

  const getColor = (pct: number) => {
    if (pct > 70) return "#10b981";
    if (pct > 40) return "#284899";
    return "#ef4444";
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-center">
        <div className="relative w-32 h-16 overflow-hidden">
          <svg width="128" height="80" viewBox="0 0 128 80">
            <path
              d="M 10 70 A 54 54 0 0 1 118 70"
              fill="none"
              stroke="hsl(var(--secondary))"
              strokeWidth="12"
              strokeLinecap="round"
            />
            <motion.path
              d="M 10 70 A 54 54 0 0 1 118 70"
              fill="none"
              stroke={getColor(avgCompleteness)}
              strokeWidth="12"
              strokeLinecap="round"
              strokeDasharray="170"
              initial={{ strokeDashoffset: 170 }}
              animate={{ strokeDashoffset: 170 - (avgCompleteness / 100) * 170 }}
              transition={{ duration: 1, ease: "easeOut" }}
            />
          </svg>
          <div className="absolute bottom-0 left-1/2 -translate-x-1/2 text-center">
            <span className="text-xl font-bold text-foreground tabular-nums">{avgCompleteness.toFixed(0)}%</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-5 gap-1.5">
        {data.map((item, idx) => (
          <motion.div
            key={idx}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.1 }}
            className="flex flex-col items-center p-2 rounded-lg bg-secondary/40"
          >
            <span className="text-xs font-bold tabular-nums" style={{ color: getColor(item.percentage) }}>
              {item.percentage.toFixed(0)}%
            </span>
            <span className="text-[12px] text-muted-foreground truncate w-full text-center mt-0.5">
              {item.label}
            </span>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// TYPOLOGY BARS - Gradient horizontal bars with icons
// ═══════════════════════════════════════════════════════════════
function TypologyBars({ items }: { items: StatItem[] }) {
  const max = Math.max(...items.map(i => i.value), 1);

  return (
    <div className="space-y-2">
      {items.map((item, idx) => {
        const IconComponent = item.icon;
        const width = (item.value / max) * 100;

        return (
          <div key={idx} className="group">
            <div className="flex items-center gap-2 mb-1">
              {IconComponent && (
                <IconComponent
                  className="h-3.5 w-3.5 flex-shrink-0 transition-transform group-hover:scale-110"
                  style={{ color: item.color }}
                />
              )}
              <span className="text-[12px] text-muted-foreground truncate flex-1" title={item.label}>
                {item.label}
              </span>
              <span className="text-[12px] font-semibold text-foreground tabular-nums">
                {item.value.toLocaleString()}
              </span>
            </div>
            <div className="h-2.5 bg-secondary rounded-full overflow-hidden">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${width}%` }}
                transition={{ duration: 0.5, delay: idx * 0.05, ease: "easeOut" }}
                className="h-full rounded-full"
                style={{ backgroundColor: item.color || "#284899" }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// EVENTS TIMELINE - Visual timeline for events
// ═══════════════════════════════════════════════════════════════
function EventsTimeline({ events }: { events: { withDates: number; upcoming: number; past: number; active: number } }) {
  const items = [
    { label: "Passati", value: events.past, color: "#64748b" },
    { label: "Attivi", value: events.active, color: "#10b981" },
    { label: "In arrivo", value: events.upcoming, color: "#3b82f6" },
  ];

  const total = events.withDates;

  return (
    <div className="space-y-3">
      <div className="flex h-6 rounded-full overflow-hidden bg-secondary">
        {items.map((item, idx) => {
          const width = total > 0 ? (item.value / total) * 100 : 0;
          if (width < 1) return null;

          return (
            <motion.div
              key={idx}
              initial={{ width: 0 }}
              animate={{ width: `${width}%` }}
              transition={{ duration: 0.6, delay: idx * 0.15 }}
              className="h-full flex items-center justify-center"
              style={{ backgroundColor: item.color }}
            >
              {width > 10 && (
                <span className="text-[12px] font-medium text-white tabular-nums">
                  {item.value}
                </span>
              )}
            </motion.div>
          );
        })}
      </div>

      <div className="flex justify-around">
        {items.map((item, idx) => (
          <motion.div
            key={idx}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: idx * 0.1 + 0.3 }}
            className="flex flex-col items-center"
          >
            <div
              className="w-8 h-8 rounded-full flex items-center justify-center mb-1"
              style={{ backgroundColor: `${item.color}40` }}
            >
              <span className="text-lg font-bold tabular-nums" style={{ color: item.color }}>
                {item.value}
              </span>
            </div>
            <span className="text-[12px] text-muted-foreground">{item.label}</span>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════
export function AnalyticsCard({ allOperators, filteredOperators, operatorsInView, isOpen, onClose }: AnalyticsCardProps) {
  const [activeTab, setActiveTab] = useState<"overview" | "categories" | "regions" | "quality">("overview");
  const [isExporting, setIsExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState(0);
  const [isExportMenuOpen, setIsExportMenuOpen] = useState(false);
  const [remoteCompleteness, setRemoteCompleteness] = useState<CompletenessResult | null>(null);
  const { token } = useAuthStore();

  // Carica i conteggi reali dei campi (email/phone/website/photo/description) via RPC
  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    (async () => {
      try {
        const rows = await queryOperatorsProtected<Array<{
          total: number | string;
          with_email: number | string;
          with_phone: number | string;
          with_website: number | string;
          with_photo: number | string;
          with_description: number | string;
        }>>(token, "completeness_stats", {});
        if (cancelled || !rows?.length) return;
        const r = rows[0];
        const total = Number(r.total) || 0;
        const pct = (n: number) => (total > 0 ? (n / total) * 100 : 0);
        const email = Number(r.with_email) || 0;
        const phone = Number(r.with_phone) || 0;
        const website = Number(r.with_website) || 0;
        const photo = Number(r.with_photo) || 0;
        const description = Number(r.with_description) || 0;
        setRemoteCompleteness({
          total,
          email: { count: email, pct: pct(email) },
          phone: { count: phone, pct: pct(phone) },
          website: { count: website, pct: pct(website) },
          photo: { count: photo, pct: pct(photo) },
          description: { count: description, pct: pct(description) },
        });
      } catch {
        // Se la RPC fallisce, resta il fallback locale (che sarà 0% per i campi assenti)
      }
    })();
    return () => { cancelled = true; };
  }, [token]);
  const contentRef = useRef<HTMLDivElement>(null);
  const exportMenuRef = useRef<HTMLDivElement>(null);
  const resolvedTheme = useThemeStore((state) => state.resolvedTheme);

  const hasFilters = filteredOperators.length !== allOperators.length;
  const statsOperators = hasFilters ? filteredOperators : allOperators;

  // Close export menu on click outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (exportMenuRef.current && !exportMenuRef.current.contains(event.target as Node)) {
        setIsExportMenuOpen(false);
      }
    }

    if (isExportMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isExportMenuOpen]);

  const stats = useMemo(() => {
    return {
      categories: computeCategoryStats(statsOperators),
      typologies: computeTypologyStats(statsOperators),
      regions: computeRegionStats(statsOperators),
      stars: computeStarsStats(statsOperators),
      sources: computeSourceStats(statsOperators),
      events: computeEventsStats(statsOperators),
      completeness: remoteCompleteness ?? computeCompletenessStats(statsOperators),
      total: statsOperators.length,
      inView: operatorsInView.length,
      all: allOperators.length,
    };
  }, [statsOperators, operatorsInView, allOperators, remoteCompleteness]);

  // ═══════════════════════════════════════════════════════════════
  // EXPORT CSV - Genera CSV con tutti i dati statistici
  // ═══════════════════════════════════════════════════════════════
  const exportToCSV = useCallback(() => {
    const timestamp = new Date().toISOString().split("T")[0];
    const filename = `statistiche_dpm_${timestamp}.csv`;

    const lines: string[] = [];

    // Header info
    lines.push("DPM - Destination Plan Machine - Report Statistiche");
    lines.push(`Data Export: ${new Date().toLocaleDateString("it-IT")}`);
    lines.push(`Operatori Totali: ${stats.all.toLocaleString()}`);
    lines.push(`Operatori Filtrati: ${stats.total.toLocaleString()}`);
    lines.push(`Operatori Visibili: ${stats.inView.toLocaleString()}`);
    lines.push("");

    // Categorie
    lines.push("═══════════════════════════════════════════════════════════════");
    lines.push("DISTRIBUZIONE PER CATEGORIA");
    lines.push("═══════════════════════════════════════════════════════════════");
    lines.push("Categoria;Valore Raw;Conteggio;Percentuale");
    for (const cat of stats.categories) {
      lines.push(`${cat.label};${cat.rawLabel || cat.label};${cat.value};${cat.percentage?.toFixed(2)}%`);
    }
    lines.push("");

    // Tipologie
    lines.push("═══════════════════════════════════════════════════════════════");
    lines.push("DISTRIBUZIONE PER TIPOLOGIA");
    lines.push("═══════════════════════════════════════════════════════════════");
    lines.push("Tipologia;Valore Raw;Conteggio;Percentuale");
    for (const typ of stats.typologies) {
      lines.push(`${typ.label};${typ.rawLabel || typ.label};${typ.value};${typ.percentage?.toFixed(2)}%`);
    }
    lines.push("");

    // Regioni
    lines.push("═══════════════════════════════════════════════════════════════");
    lines.push("DISTRIBUZIONE PER REGIONE");
    lines.push("═══════════════════════════════════════════════════════════════");
    lines.push("Regione;Conteggio;Percentuale");
    for (const reg of stats.regions) {
      lines.push(`${reg.label};${reg.value};${reg.percentage?.toFixed(2)}%`);
    }
    lines.push("");

    // Stelle
    lines.push("═══════════════════════════════════════════════════════════════");
    lines.push("DISTRIBUZIONE STELLE");
    lines.push("═══════════════════════════════════════════════════════════════");
    lines.push(`Media Stelle: ${stats.stars.avgStars.toFixed(2)}`);
    lines.push(`Operatori con Rating: ${stats.stars.withStars.toLocaleString()}`);
    lines.push("Stelle;Conteggio;Percentuale");
    for (const star of stats.stars.distribution) {
      lines.push(`${star.label};${star.value};${star.percentage?.toFixed(2)}%`);
    }
    lines.push("");

    // Eventi
    if (stats.events.withDates > 0) {
      lines.push("═══════════════════════════════════════════════════════════════");
      lines.push("EVENTI TEMPORALI");
      lines.push("═══════════════════════════════════════════════════════════════");
      lines.push(`Con Date: ${stats.events.withDates}`);
      lines.push(`Passati: ${stats.events.past}`);
      lines.push(`Attivi: ${stats.events.active}`);
      lines.push(`In Arrivo: ${stats.events.upcoming}`);
      lines.push("");
    }

    // Completezza
    lines.push("═══════════════════════════════════════════════════════════════");
    lines.push("COMPLETEZZA DATI");
    lines.push("═══════════════════════════════════════════════════════════════");
    lines.push("Campo;Conteggio;Percentuale");
    lines.push(`Email;${stats.completeness.email.count};${stats.completeness.email.pct.toFixed(2)}%`);
    lines.push(`Telefono;${stats.completeness.phone.count};${stats.completeness.phone.pct.toFixed(2)}%`);
    lines.push(`Sito Web;${stats.completeness.website.count};${stats.completeness.website.pct.toFixed(2)}%`);
    lines.push(`Foto;${stats.completeness.photo.count};${stats.completeness.photo.pct.toFixed(2)}%`);
    lines.push(`Descrizione;${stats.completeness.description.count};${stats.completeness.description.pct.toFixed(2)}%`);

    // Crea e scarica il file
    const csvContent = lines.join("\n");
    const blob = new Blob(["\ufeff" + csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  }, [stats]);

  // ═══════════════════════════════════════════════════════════════
  // EXPORT IMAGE - Genera immagine con tutte le statistiche in formato A4
  // ═══════════════════════════════════════════════════════════════
  const exportToImage = useCallback(async () => {
    setIsExporting(true);
    setExportProgress(0);

    // Usa i colori del tema corrente
    const theme = exportThemes[resolvedTheme];

    // Dimensioni A4 in pixel a 300 DPI (qualità stampa)
    // A4: 210mm × 297mm = 8.27" × 11.69" = 2480px × 3508px @ 300 DPI
    const A4_WIDTH = 2480;
    const PADDING = 80; // Margini generosi per la stampa

    setExportProgress(10);

    // Crea un container temporaneo con tutte le statistiche.
    // Manteniamo il nodo visibile (ma fuori dallo schermo) così html-to-image lo renderizza correttamente.
    const exportContainer = document.createElement("div");
    exportContainer.style.cssText = `
      position: fixed;
      left: -99999px;
      top: -99999px;
      width: ${A4_WIDTH}px;
      padding: ${PADDING}px;
      background: ${theme.bg};
      color: ${theme.text};
      font-family: system-ui, -apple-system, sans-serif;
      z-index: -1;
      box-sizing: border-box;
      overflow: hidden;
      pointer-events: none;
      visibility: visible;
      opacity: 1;
    `;

    // Genera HTML con tutte le statistiche usando colori inline, ottimizzato per A4
    const hasFiltersLabel = hasFilters ? " (Filtrati)" : "";

    // Calcolo dimensioni font scalate per A4 (circa 3x rispetto a 800px)
    const scale = A4_WIDTH / 800; // ~3.1x
    const fontSize = {
      h1: Math.round(28 * scale),
      h2: Math.round(18 * scale),
      body: Math.round(14 * scale),
      small: Math.round(12 * scale),
      xs: Math.round(11 * scale),
    };

    exportContainer.innerHTML = `
      <div style="margin-bottom: ${60 * scale}px; padding-bottom: ${40 * scale}px; border-bottom: ${4 * scale}px solid #284899;">
        <h1 style="font-size: ${fontSize.h1}px; font-weight: bold; color: #284899; margin: 0 0 ${20 * scale}px 0; line-height: 1.2;">
          📊 DPM - Statistiche Operatori${hasFiltersLabel}
        </h1>
        <p style="color: ${theme.textMuted}; font-size: ${fontSize.body}px; margin: 0;">
          Export del ${new Date().toLocaleDateString("it-IT")} • ${stats.total.toLocaleString()} operatori analizzati
        </p>
      </div>
      
      <!-- PANORAMICA -->
      <div style="margin-bottom: ${60 * scale}px;">
        <h2 style="font-size: ${fontSize.h2}px; font-weight: 600; color: #3b82f6; margin: 0 0 ${30 * scale}px 0;">
          📈 Panoramica
        </h2>
        <div style="display: flex; gap: ${30 * scale}px; margin-bottom: ${30 * scale}px; width: 100%; box-sizing: border-box;">
          <div style="background: ${theme.cardBg}; padding: ${30 * scale}px ${30 * scale}px; border-radius: ${20 * scale}px; text-align: center; flex: 1; min-width: 0; box-sizing: border-box;">
            <div style="font-size: ${60 * scale}px; font-weight: bold; color: #3b82f6; line-height: 1; word-break: break-word;">${stats.all.toLocaleString()}</div>
            <div style="font-size: ${fontSize.small}px; color: ${theme.textMuted}; margin-top: ${10 * scale}px;">Totali</div>
          </div>
          <div style="background: ${theme.cardBg}; padding: ${30 * scale}px ${30 * scale}px; border-radius: ${20 * scale}px; text-align: center; flex: 1; min-width: 0; box-sizing: border-box;">
            <div style="font-size: ${60 * scale}px; font-weight: bold; color: #284899; line-height: 1; word-break: break-word;">${stats.total.toLocaleString()}</div>
            <div style="font-size: ${fontSize.small}px; color: ${theme.textMuted}; margin-top: ${10 * scale}px;">${hasFilters ? "Filtrati" : "Analizzati"}</div>
          </div>
          <div style="background: ${theme.cardBg}; padding: ${30 * scale}px ${30 * scale}px; border-radius: ${20 * scale}px; text-align: center; flex: 1; min-width: 0; box-sizing: border-box;">
            <div style="font-size: ${60 * scale}px; font-weight: bold; color: #10b981; line-height: 1; word-break: break-word;">${stats.inView.toLocaleString()}</div>
            <div style="font-size: ${fontSize.small}px; color: ${theme.textMuted}; margin-top: ${10 * scale}px;">Visibili</div>
          </div>
        </div>
      </div>
      
      <!-- CATEGORIE -->
      <div style="margin-bottom: ${60 * scale}px;">
        <h2 style="font-size: ${fontSize.h2}px; font-weight: 600; color: #284899; margin: 0 0 ${30 * scale}px 0;">
          🏢 Distribuzione per Categoria
        </h2>
        <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: ${20 * scale}px;">
          ${stats.categories.map((cat) => `
            <div style="background: ${theme.cardBg}; padding: ${20 * scale}px ${30 * scale}px; border-radius: ${15 * scale}px; display: flex; justify-content: space-between; align-items: center;">
              <span style="color: ${theme.text}; font-size: ${fontSize.body}px;">${cat.label}</span>
              <div style="text-align: right;">
                <span style="color: #284899; font-weight: bold; font-size: ${fontSize.body}px;">${cat.value.toLocaleString()}</span>
                <span style="color: ${theme.textMuted}; font-size: ${fontSize.small}px; margin-left: ${12 * scale}px;">${cat.percentage?.toFixed(1)}%</span>
              </div>
            </div>
          `).join("")}
        </div>
      </div>
      
      <!-- TIPOLOGIE -->
      <div style="margin-bottom: ${60 * scale}px;">
        <h2 style="font-size: ${fontSize.h2}px; font-weight: 600; color: #8b5cf6; margin: 0 0 ${30 * scale}px 0;">
          🏷️ Distribuzione per Tipologia
        </h2>
        <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: ${20 * scale}px;">
          ${stats.typologies.slice(0, 10).map((typ) => `
            <div style="background: ${theme.cardBg}; padding: ${20 * scale}px ${30 * scale}px; border-radius: ${15 * scale}px; display: flex; justify-content: space-between; align-items: center;">
              <span style="color: ${theme.text}; font-size: ${fontSize.body}px; max-width: ${400 * scale}px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;">${typ.label}</span>
              <div style="text-align: right;">
                <span style="color: #8b5cf6; font-weight: bold; font-size: ${fontSize.body}px;">${typ.value.toLocaleString()}</span>
                <span style="color: ${theme.textMuted}; font-size: ${fontSize.small}px; margin-left: ${12 * scale}px;">${typ.percentage?.toFixed(1)}%</span>
              </div>
            </div>
          `).join("")}
        </div>
      </div>
      
      <!-- REGIONI -->
      <div style="margin-bottom: ${60 * scale}px;">
        <h2 style="font-size: ${fontSize.h2}px; font-weight: 600; color: #10b981; margin: 0 0 ${30 * scale}px 0;">
          📍 Distribuzione per Regione (Top 10)
        </h2>
        <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: ${20 * scale}px;">
          ${stats.regions.slice(0, 10).map((reg, i) => `
            <div style="background: ${theme.cardBg}; padding: ${20 * scale}px ${30 * scale}px; border-radius: ${15 * scale}px; display: flex; justify-content: space-between; align-items: center;">
              <span style="color: ${theme.text}; font-size: ${fontSize.body}px;">
                <span style="color: ${i < 3 ? "#284899" : theme.textMuted}; margin-right: ${12 * scale}px; font-weight: ${i < 3 ? "bold" : "normal"};">#${i + 1}</span>
                ${reg.label}
              </span>
              <div style="text-align: right;">
                <span style="color: #10b981; font-weight: bold; font-size: ${fontSize.body}px;">${reg.value.toLocaleString()}</span>
                <span style="color: ${theme.textMuted}; font-size: ${fontSize.small}px; margin-left: ${12 * scale}px;">${reg.percentage?.toFixed(1)}%</span>
              </div>
            </div>
          `).join("")}
        </div>
      </div>
      
      <!-- QUALITÀ -->
      <div style="margin-bottom: ${60 * scale}px;">
        <h2 style="font-size: ${fontSize.h2}px; font-weight: 600; color: #eab308; margin: 0 0 ${30 * scale}px 0;">
          ⭐ Stelle
        </h2>
        <div style="display: flex; gap: ${40 * scale}px; align-items: center; margin-bottom: ${30 * scale}px;">
          <div style="background: #284899; padding: ${40 * scale}px ${50 * scale}px; border-radius: ${25 * scale}px; text-align: center; min-width: ${200 * scale}px;">
            <div style="font-size: ${70 * scale}px; font-weight: bold; color: #ffffff; line-height: 1;">⭐ ${stats.stars.avgStars.toFixed(1)}</div>
            <div style="font-size: ${fontSize.small}px; color: #ffffff; opacity: 0.9; margin-top: ${10 * scale}px;">Media stelle</div>
          </div>
          <div style="flex: 1;">
            ${stats.stars.distribution.map((star) => `
              <div style="display: flex; align-items: center; gap: ${20 * scale}px; margin-bottom: ${15 * scale}px;">
                <span style="color: ${theme.text}; width: ${120 * scale}px; font-size: ${fontSize.body}px;">${star.label}</span>
                <div style="flex: 1; height: ${30 * scale}px; background: ${theme.cardBg}; border-radius: ${15 * scale}px; overflow: hidden;">
                  <div style="width: ${star.percentage}%; height: 100%; background: #284899;"></div>
                </div>
                <span style="color: ${theme.text}; width: ${100 * scale}px; text-align: right; font-size: ${fontSize.body}px; font-weight: 600;">${star.value.toLocaleString()}</span>
              </div>
            `).join("")}
          </div>
        </div>
        <p style="color: ${theme.textMuted}; font-size: ${fontSize.body}px; margin: 0;">
          ${stats.stars.withStars.toLocaleString()} operatori con stelle
        </p>
      </div>
      
      <!-- COMPLETEZZA DATI -->
      <div style="margin-bottom: ${50 * scale}px;">
        <h2 style="font-size: ${fontSize.h2}px; font-weight: 600; color: #06b6d4; margin: 0 0 ${30 * scale}px 0;">
          📊 Completezza Dati
        </h2>
        <div style="display: flex; gap: ${25 * scale}px; flex-wrap: wrap;">
          ${[
        { label: "Email", pct: stats.completeness.email.pct, count: stats.completeness.email.count },
        { label: "Telefono", pct: stats.completeness.phone.pct, count: stats.completeness.phone.count },
        { label: "Sito Web", pct: stats.completeness.website.pct, count: stats.completeness.website.count },
        { label: "Foto", pct: stats.completeness.photo.pct, count: stats.completeness.photo.count },
        { label: "Descrizione", pct: stats.completeness.description.pct, count: stats.completeness.description.count },
      ].map((item) => `
            <div style="background: ${theme.cardBg}; padding: ${25 * scale}px ${30 * scale}px; border-radius: ${15 * scale}px; text-align: center; min-width: ${180 * scale}px;">
              <div style="font-size: ${45 * scale}px; font-weight: bold; color: ${item.pct > 70 ? "#10b981" : item.pct > 40 ? "#284899" : "#ef4444"}; line-height: 1;">
                ${item.pct.toFixed(0)}%
              </div>
              <div style="font-size: ${fontSize.small}px; color: ${theme.textMuted}; margin-top: ${8 * scale}px;">${item.label}</div>
              <div style="font-size: ${fontSize.xs}px; color: ${theme.textSubtle}; margin-top: ${5 * scale}px;">${item.count.toLocaleString()}</div>
            </div>
          `).join("")}
        </div>
      </div>
      
      <!-- FOOTER -->
      <div style="padding-top: ${30 * scale}px; border-top: ${2 * scale}px solid ${theme.border}; text-align: center; margin-top: auto;">
        <p style="color: ${theme.textSubtle}; font-size: ${fontSize.small}px; margin: 0;">
          DPM - Destination Plan Machine • Report generato il ${new Date().toLocaleString("it-IT")}
        </p>
      </div>
    `;

    document.body.appendChild(exportContainer);
    setExportProgress(30);

    // Forza il rendering del contenuto - attendi che le immagini e i font siano caricati
    await new Promise(resolve => {
      // Trigger reflow
      exportContainer.getBoundingClientRect();
      setExportProgress(50);

      // Attendi che il browser renderizzi completamente
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          setTimeout(() => {
            setExportProgress(70);
            resolve(undefined);
          }, 500);
        });
      });
    });

    try {
      setExportProgress(80);

      // Calcola l'altezza effettiva del contenuto
      const actualHeight = Math.max(
        exportContainer.offsetHeight || 0,
        exportContainer.scrollHeight || 0,
        exportContainer.clientHeight || 0
      );

      if (actualHeight === 0) {
        throw new Error("Impossibile calcolare l'altezza del contenuto");
      }

      setExportProgress(90);

      const dataUrl = await toPng(exportContainer, {
        backgroundColor: theme.bg,
        pixelRatio: 1, // A4 già a 300 DPI, non serve aumentare
        cacheBust: true,
        width: A4_WIDTH,
        height: actualHeight,
        skipAutoScale: false, // Permetti lo scaling se necessario
        style: {
          transform: "none",
        },
      });

      setExportProgress(95);

      const timestamp = new Date().toISOString().split("T")[0];
      const filename = `statistiche_dpm_A4_${timestamp}.png`;

      const link = document.createElement("a");
      link.download = filename;
      link.href = dataUrl;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      setExportProgress(100);
    } catch (error) {
      console.error("Errore durante l'export dell'immagine:", error);
      alert("Errore durante l'export dell'immagine. Riprova.");
    } finally {
      document.body.removeChild(exportContainer);
      setTimeout(() => {
        setIsExporting(false);
        setExportProgress(0);
      }, 300);
    }
  }, [stats, hasFilters, resolvedTheme]);

  // ═══════════════════════════════════════════════════════════════
  // EXPORT PDF - Genera PDF con tutte le statistiche in formato A4
  // ═══════════════════════════════════════════════════════════════
  const exportToPDF = useCallback(async () => {
    setIsExporting(true);
    setExportProgress(0);

    try {
      setExportProgress(10);

      // Crea il PDF usando jsPDF con testo nativo (no conversione immagine)
      const pdf = new jsPDF({
        orientation: "portrait",
        unit: "mm",
        format: "a4",
      });

      // Colori basati sul tema
      const isDark = resolvedTheme === "dark";
      const colors = {
        primary: [40, 72, 153], // blue-500 (#284899)
        secondary: [59, 130, 246], // blue-500
        success: [16, 185, 129], // emerald-500
        warning: [251, 146, 60], // orange-500
        text: isDark ? [255, 255, 255] : [24, 24, 27],
        textMuted: isDark ? [161, 161, 170] : [82, 82, 91],
        bg: isDark ? [26, 26, 26] : [255, 255, 255],
        cardBg: isDark ? [38, 38, 38] : [244, 244, 245],
      };

      // Dimensioni pagina A4
      const pageWidth = pdf.internal.pageSize.getWidth(); // 210mm
      const pageHeight = pdf.internal.pageSize.getHeight(); // 297mm
      const margin = 15;
      const contentWidth = pageWidth - (margin * 2);
      let yPos = margin;

      // Funzione per controllare lo spazio rimanente e aggiungere pagina se necessario
      const checkPageBreak = (spaceNeeded: number) => {
        if (yPos + spaceNeeded > pageHeight - margin) {
          pdf.addPage();
          yPos = margin;
          return true;
        }
        return false;
      };

      setExportProgress(20);

      // ═══ HEADER ═══
      pdf.setFillColor(colors.bg[0], colors.bg[1], colors.bg[2]);
      pdf.rect(0, 0, pageWidth, pageHeight, 'F');

      // Titolo
      pdf.setFontSize(24);
      pdf.setTextColor(colors.primary[0], colors.primary[1], colors.primary[2]);
      pdf.setFont("helvetica", "bold");
      const hasFiltersLabel = hasFilters ? " (Filtrati)" : "";
      pdf.text(`DPM - Statistiche Operatori${hasFiltersLabel}`, margin, yPos);
      yPos += 10;

      // Sottotitolo
      pdf.setFontSize(10);
      pdf.setTextColor(colors.textMuted[0], colors.textMuted[1], colors.textMuted[2]);
      pdf.setFont("helvetica", "normal");
      pdf.text(`Export del ${new Date().toLocaleDateString("it-IT")} - ${stats.total.toLocaleString()} operatori analizzati`, margin, yPos);
      yPos += 3;

      // Linea separatrice
      pdf.setDrawColor(colors.primary[0], colors.primary[1], colors.primary[2]);
      pdf.setLineWidth(0.5);
      pdf.line(margin, yPos, pageWidth - margin, yPos);
      yPos += 10;

      setExportProgress(30);

      // ═══ PANORAMICA ═══
      checkPageBreak(40);
      pdf.setFontSize(16);
      pdf.setTextColor(colors.secondary[0], colors.secondary[1], colors.secondary[2]);
      pdf.setFont("helvetica", "bold");
      pdf.text("PANORAMICA", margin, yPos);
      yPos += 10;

      // Box con i 3 valori principali
      const boxWidth = (contentWidth - 10) / 3;
      const boxHeight = 25;
      const boxY = yPos;

      // Box Totali
      pdf.setFillColor(colors.cardBg[0], colors.cardBg[1], colors.cardBg[2]);
      pdf.roundedRect(margin, boxY, boxWidth, boxHeight, 3, 3, 'F');
      pdf.setFontSize(20);
      pdf.setTextColor(colors.secondary[0], colors.secondary[1], colors.secondary[2]);
      pdf.setFont("helvetica", "bold");
      pdf.text(stats.all.toLocaleString(), margin + boxWidth / 2, boxY + 12, { align: "center" });
      pdf.setFontSize(9);
      pdf.setTextColor(colors.textMuted[0], colors.textMuted[1], colors.textMuted[2]);
      pdf.setFont("helvetica", "normal");
      pdf.text("Totali", margin + boxWidth / 2, boxY + 19, { align: "center" });

      // Box Filtrati/Analizzati
      pdf.setFillColor(colors.cardBg[0], colors.cardBg[1], colors.cardBg[2]);
      pdf.roundedRect(margin + boxWidth + 5, boxY, boxWidth, boxHeight, 3, 3, 'F');
      pdf.setFontSize(20);
      pdf.setTextColor(colors.primary[0], colors.primary[1], colors.primary[2]);
      pdf.setFont("helvetica", "bold");
      pdf.text(stats.total.toLocaleString(), margin + boxWidth + 5 + boxWidth / 2, boxY + 12, { align: "center" });
      pdf.setFontSize(9);
      pdf.setTextColor(colors.textMuted[0], colors.textMuted[1], colors.textMuted[2]);
      pdf.setFont("helvetica", "normal");
      pdf.text(hasFilters ? "Filtrati" : "Analizzati", margin + boxWidth + 5 + boxWidth / 2, boxY + 19, { align: "center" });

      // Box Visibili
      pdf.setFillColor(colors.cardBg[0], colors.cardBg[1], colors.cardBg[2]);
      pdf.roundedRect(margin + (boxWidth + 5) * 2, boxY, boxWidth, boxHeight, 3, 3, 'F');
      pdf.setFontSize(20);
      pdf.setTextColor(colors.success[0], colors.success[1], colors.success[2]);
      pdf.setFont("helvetica", "bold");
      pdf.text(stats.inView.toLocaleString(), margin + (boxWidth + 5) * 2 + boxWidth / 2, boxY + 12, { align: "center" });
      pdf.setFontSize(9);
      pdf.setTextColor(colors.textMuted[0], colors.textMuted[1], colors.textMuted[2]);
      pdf.setFont("helvetica", "normal");
      pdf.text("Visibili", margin + (boxWidth + 5) * 2 + boxWidth / 2, boxY + 19, { align: "center" });

      yPos += boxHeight + 15;

      setExportProgress(40);

      // ═══ CATEGORIE ═══
      checkPageBreak(60);
      pdf.setFontSize(14);
      pdf.setTextColor(colors.primary[0], colors.primary[1], colors.primary[2]);
      pdf.setFont("helvetica", "bold");
      pdf.text("DISTRIBUZIONE PER CATEGORIA", margin, yPos);
      yPos += 8;

      pdf.setFontSize(9);
      pdf.setFont("helvetica", "normal");
      stats.categories.forEach((cat) => {
        if (checkPageBreak(8)) {
          pdf.setFontSize(9);
          pdf.setFont("helvetica", "normal");
        }

        // Riga categoria
        pdf.setFillColor(colors.cardBg[0], colors.cardBg[1], colors.cardBg[2]);
        pdf.roundedRect(margin, yPos - 5, contentWidth, 7, 1, 1, 'F');

        pdf.setTextColor(colors.text[0], colors.text[1], colors.text[2]);
        pdf.text(cat.label, margin + 2, yPos);

        pdf.setTextColor(colors.primary[0], colors.primary[1], colors.primary[2]);
        pdf.setFont("helvetica", "bold");
        const valueText = `${cat.value.toLocaleString()} (${cat.percentage?.toFixed(1)}%)`;
        pdf.text(valueText, pageWidth - margin - 2, yPos, { align: "right" });
        pdf.setFont("helvetica", "normal");

        yPos += 9;
      });

      yPos += 7;
      setExportProgress(50);

      // ═══ TIPOLOGIE ═══
      checkPageBreak(60);
      pdf.setFontSize(14);
      pdf.setTextColor(139, 92, 246); // violet-500
      pdf.setFont("helvetica", "bold");
      pdf.text("DISTRIBUZIONE PER TIPOLOGIA", margin, yPos);
      yPos += 8;

      pdf.setFontSize(9);
      pdf.setFont("helvetica", "normal");
      stats.typologies.slice(0, 10).forEach((typ) => {
        if (checkPageBreak(8)) {
          pdf.setFontSize(9);
          pdf.setFont("helvetica", "normal");
        }

        pdf.setFillColor(colors.cardBg[0], colors.cardBg[1], colors.cardBg[2]);
        pdf.roundedRect(margin, yPos - 5, contentWidth, 7, 1, 1, 'F');

        pdf.setTextColor(colors.text[0], colors.text[1], colors.text[2]);
        // Tronca il testo se troppo lungo
        const label = typ.label.length > 35 ? typ.label.substring(0, 32) + "..." : typ.label;
        pdf.text(label, margin + 2, yPos);

        pdf.setTextColor(139, 92, 246);
        pdf.setFont("helvetica", "bold");
        const valueText = `${typ.value.toLocaleString()} (${typ.percentage?.toFixed(1)}%)`;
        pdf.text(valueText, pageWidth - margin - 2, yPos, { align: "right" });
        pdf.setFont("helvetica", "normal");

        yPos += 9;
      });

      yPos += 7;
      setExportProgress(60);

      // ═══ REGIONI ═══
      checkPageBreak(60);
      pdf.setFontSize(14);
      pdf.setTextColor(colors.success[0], colors.success[1], colors.success[2]);
      pdf.setFont("helvetica", "bold");
      pdf.text("DISTRIBUZIONE PER REGIONE (Top 10)", margin, yPos);
      yPos += 8;

      pdf.setFontSize(9);
      pdf.setFont("helvetica", "normal");
      stats.regions.slice(0, 10).forEach((reg, idx) => {
        if (checkPageBreak(8)) {
          pdf.setFontSize(9);
          pdf.setFont("helvetica", "normal");
        }

        pdf.setFillColor(colors.cardBg[0], colors.cardBg[1], colors.cardBg[2]);
        pdf.roundedRect(margin, yPos - 5, contentWidth, 7, 1, 1, 'F');

        // Numero ranking
        const rankColor = idx < 3 ? colors.primary : colors.textMuted;
        pdf.setTextColor(rankColor[0], rankColor[1], rankColor[2]);
        pdf.setFont("helvetica", "bold");
        pdf.text(`#${idx + 1}`, margin + 2, yPos);

        pdf.setTextColor(colors.text[0], colors.text[1], colors.text[2]);
        pdf.setFont("helvetica", "normal");
        pdf.text(reg.label, margin + 10, yPos);

        pdf.setTextColor(colors.success[0], colors.success[1], colors.success[2]);
        pdf.setFont("helvetica", "bold");
        const valueText = `${reg.value.toLocaleString()} (${reg.percentage?.toFixed(1)}%)`;
        pdf.text(valueText, pageWidth - margin - 2, yPos, { align: "right" });
        pdf.setFont("helvetica", "normal");

        yPos += 9;
      });

      yPos += 7;
      setExportProgress(70);

      // ═══ STELLE ═══
      checkPageBreak(50);
      pdf.setFontSize(14);
      pdf.setTextColor(234, 179, 8); // yellow-500
      pdf.setFont("helvetica", "bold");
      pdf.text("STELLE", margin, yPos);
      yPos += 8;

      // Box media stelle
      pdf.setFillColor(colors.primary[0], colors.primary[1], colors.primary[2]);
      pdf.roundedRect(margin, yPos, 50, 20, 3, 3, 'F');
      pdf.setFontSize(18);
      pdf.setTextColor(255, 255, 255);
      pdf.text(`${stats.stars.avgStars.toFixed(1)}`, margin + 25, yPos + 10, { align: "center" });
      pdf.setFontSize(8);
      pdf.text("Media stelle", margin + 25, yPos + 16, { align: "center" });

      // Distribuzione stelle
      const barStartX = margin + 60;
      const barWidth = contentWidth - 60;
      let barY = yPos + 2;

      pdf.setFontSize(8);
      stats.stars.distribution.forEach((star) => {
        pdf.setTextColor(colors.text[0], colors.text[1], colors.text[2]);
        pdf.setFont("helvetica", "normal");
        pdf.text(star.label, barStartX, barY);

        // Barra percentuale
        const barLength = ((star.percentage || 0) / 100) * (barWidth - 35);
        pdf.setFillColor(colors.primary[0], colors.primary[1], colors.primary[2]);
        pdf.rect(barStartX + 18, barY - 3, barLength, 4, 'F');

        // Valore
        pdf.setFont("helvetica", "bold");
        pdf.text(star.value.toLocaleString(), barStartX + barWidth - 10, barY, { align: "right" });

        barY += 6;
      });

      yPos += 30;
      pdf.setFontSize(8);
      pdf.setTextColor(colors.textMuted[0], colors.textMuted[1], colors.textMuted[2]);
      pdf.setFont("helvetica", "normal");
      pdf.text(`${stats.stars.withStars.toLocaleString()} operatori con stelle`, margin, yPos);
      yPos += 10;

      setExportProgress(80);

      // ═══ COMPLETEZZA DATI ═══
      checkPageBreak(40);
      pdf.setFontSize(14);
      pdf.setTextColor(6, 182, 212); // cyan-500
      pdf.setFont("helvetica", "bold");
      pdf.text("COMPLETEZZA DATI", margin, yPos);
      yPos += 10;

      const completenessData = [
        { label: "Email", ...stats.completeness.email },
        { label: "Telefono", ...stats.completeness.phone },
        { label: "Sito Web", ...stats.completeness.website },
        { label: "Foto", ...stats.completeness.photo },
        { label: "Descrizione", ...stats.completeness.description },
      ];

      const itemWidth = (contentWidth - 20) / 5;
      let itemX = margin;

      completenessData.forEach((item) => {
        // Box completezza
        pdf.setFillColor(colors.cardBg[0], colors.cardBg[1], colors.cardBg[2]);
        pdf.roundedRect(itemX, yPos, itemWidth - 2, 20, 2, 2, 'F');

        // Percentuale con colore basato sul valore
        const color = item.pct > 70 ? colors.success : item.pct > 40 ? colors.primary : [239, 68, 68];
        pdf.setFontSize(16);
        pdf.setTextColor(color[0], color[1], color[2]);
        pdf.setFont("helvetica", "bold");
        pdf.text(`${item.pct.toFixed(0)}%`, itemX + itemWidth / 2 - 1, yPos + 9, { align: "center" });

        // Label
        pdf.setFontSize(7);
        pdf.setTextColor(colors.textMuted[0], colors.textMuted[1], colors.textMuted[2]);
        pdf.setFont("helvetica", "normal");
        pdf.text(item.label, itemX + itemWidth / 2 - 1, yPos + 14, { align: "center" });

        // Conteggio
        pdf.setFontSize(6);
        pdf.setTextColor(colors.textMuted[0], colors.textMuted[1], colors.textMuted[2]);
        pdf.text(item.count.toLocaleString(), itemX + itemWidth / 2 - 1, yPos + 18, { align: "center" });

        itemX += itemWidth + 4;
      });

      yPos += 25;
      setExportProgress(90);

      // ═══ FOOTER ═══
      const footerY = pageHeight - 15;
      pdf.setDrawColor(colors.textMuted[0], colors.textMuted[1], colors.textMuted[2]);
      pdf.setLineWidth(0.3);
      pdf.line(margin, footerY, pageWidth - margin, footerY);

      pdf.setFontSize(8);
      pdf.setTextColor(colors.textMuted[0], colors.textMuted[1], colors.textMuted[2]);
      pdf.setFont("helvetica", "normal");
      pdf.text(
        `DPM - Destination Plan Machine • Report generato il ${new Date().toLocaleString("it-IT")}`,
        pageWidth / 2,
        footerY + 5,
        { align: "center" }
      );

      setExportProgress(95);

      // Salva il PDF
      const timestamp = new Date().toISOString().split("T")[0];
      const filename = `statistiche_dpm_${timestamp}.pdf`;
      pdf.save(filename);

      setExportProgress(100);

      // Piccolo ritardo per mostrare il 100%
      setTimeout(() => {
        setIsExporting(false);
        setExportProgress(0);
      }, 300);

    } catch (error) {
      console.error("Errore durante l'export del PDF:", error);
      alert("Errore durante l'export del PDF. Riprova.");
      setIsExporting(false);
      setExportProgress(0);
    }
  }, [stats, hasFilters, resolvedTheme]);

  const tabs = [
    { id: "overview", label: "Panoramica", icon: PieChart },
    { id: "categories", label: "Categorie", icon: Layers },
    { id: "regions", label: "Regioni", icon: MapPin },
    { id: "quality", label: "Qualità", icon: Star },
  ] as const;

  return (
    <>
      {/* Progress Overlay */}
      <AnimatePresence>
        {isExporting && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm"
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-secondary border border-border rounded-xl p-8 shadow-xl max-w-md w-full mx-4"
            >
              <div className="flex flex-col items-center gap-4">
                <div className="text-center w-full">
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    Generazione report...
                  </h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    {exportProgress < 30 && "Preparazione contenuto..."}
                    {exportProgress >= 30 && exportProgress < 70 && "Rendering statistiche..."}
                    {exportProgress >= 70 && exportProgress < 85 && "Generazione documento..."}
                    {exportProgress >= 85 && exportProgress < 95 && "Finalizzazione..."}
                    {exportProgress >= 95 && "Completamento..."}
                  </p>
                  <div className="w-full bg-secondary rounded-full h-3 overflow-hidden">
                    <motion.div
                      className="h-full bg-[#284899] rounded-full"
                      initial={{ width: 0 }}
                      animate={{ width: `${exportProgress}%` }}
                      transition={{ duration: 0.3, ease: "easeOut" }}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    {exportProgress}%
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.95 }}
            transition={{ duration: 0.2 }}
            className="absolute bottom-20 left-1/2 -translate-x-1/2 z-[502] w-[440px] max-w-[calc(100vw-1.5rem)] max-h-[calc(100dvh-12rem)] sm:max-h-[calc(100dvh-10rem)] flex flex-col"
          >
            <div ref={contentRef} className="overlay-solid rounded-xl shadow-lg border border-border overflow-hidden flex flex-col h-full min-h-0">
              {/* Header */}
              <div className="px-4 py-3 border-b border-border flex items-center justify-between bg-secondary/30">
                <div className="flex items-center gap-2">
                  <BarChart3 className="h-4 w-4 text-[#284899]" />
                  <h3 className="font-semibold text-sm text-foreground">Statistiche</h3>
                  {hasFilters && (
                    <span className="text-[12px] px-1.5 py-0.5 rounded bg-[#284899]/20 text-[#1e3570] font-medium">
                      Filtrati
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1">
                  {/* Export Menu Dropdown */}
                  <div className="relative" ref={exportMenuRef}>
                    <button
                      onClick={() => setIsExportMenuOpen(!isExportMenuOpen)}
                      disabled={isExporting}
                      className={cn(
                        "p-1.5 rounded hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1",
                        isExporting && "opacity-50 cursor-not-allowed",
                        isExportMenuOpen && "bg-secondary"
                      )}
                    >
                      <Download className="h-4 w-4" />
                      <ChevronDown
                        className={cn(
                          "h-3 w-3 transition-transform",
                          isExportMenuOpen && "rotate-180"
                        )}
                      />
                    </button>

                    {/* Dropdown Menu */}
                    <AnimatePresence>
                      {isExportMenuOpen && (
                        <motion.div
                          initial={{ opacity: 0, y: -4, scale: 0.98 }}
                          animate={{ opacity: 1, y: 0, scale: 1 }}
                          exit={{ opacity: 0, y: -4, scale: 0.98 }}
                          transition={{ duration: 0.15 }}
                          className="absolute right-0 top-full mt-1 w-48 rounded-lg bg-card border border-border shadow-lg z-[1000] overflow-hidden"
                        >
                          <div className="py-1">
                            {/* Immagine PNG Option */}
                            <button
                              onClick={() => {
                                setIsExportMenuOpen(false);
                                exportToImage();
                              }}
                              disabled={isExporting}
                              className={cn(
                                "w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-secondary transition-colors",
                                isExporting && "opacity-50 cursor-not-allowed"
                              )}
                            >
                              <Image className="h-4 w-4 text-blue-500" />
                              <div className="flex-1">
                                <div className="font-medium text-foreground">Immagine PNG</div>
                                <div className="text-[12px] text-muted-foreground">Formato A4, alta qualità</div>
                              </div>
                            </button>

                            {/* CSV Excel Option */}
                            <button
                              onClick={() => {
                                setIsExportMenuOpen(false);
                                exportToCSV();
                              }}
                              disabled={isExporting}
                              className={cn(
                                "w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-secondary transition-colors",
                                isExporting && "opacity-50 cursor-not-allowed"
                              )}
                            >
                              <Download className="h-4 w-4 text-green-500" />
                              <div className="flex-1">
                                <div className="font-medium text-foreground">CSV Excel</div>
                                <div className="text-[12px] text-muted-foreground">Dati strutturati per Excel</div>
                              </div>
                            </button>

                            {/* PDF Documento Option */}
                            <button
                              onClick={() => {
                                setIsExportMenuOpen(false);
                                exportToPDF();
                              }}
                              disabled={isExporting}
                              className={cn(
                                "w-full px-3 py-2 text-left text-sm flex items-center gap-2 hover:bg-secondary transition-colors",
                                isExporting && "opacity-50 cursor-not-allowed"
                              )}
                            >
                              <FileText className="h-4 w-4 text-red-500" />
                              <div className="flex-1">
                                <div className="font-medium text-foreground">PDF Documento</div>
                                <div className="text-[12px] text-muted-foreground">Documento stampabile A4</div>
                              </div>
                            </button>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  {/* Close Button */}
                  <button
                    onClick={onClose}
                    className="p-1 rounded hover:bg-secondary text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>

              {/* Tabs */}
              <div className="px-2 py-1.5 border-b border-border flex gap-1 bg-secondary/20">
                {tabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={cn(
                      "flex-1 flex items-center justify-center gap-1.5 py-1.5 px-2 rounded-md text-[12px] font-medium transition-all",
                      activeTab === tab.id
                        ? "bg-[#284899] text-white"
                        : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
                    )}
                  >
                    <tab.icon className="h-3 w-3" />
                    {tab.label}
                  </button>
                ))}
              </div>

              {/* Content */}
              <div className="flex-1 overflow-y-auto p-4 space-y-4 min-h-0">
                {/* Overview Tab */}
                {activeTab === "overview" && (
                  <>
                    {/* Radial Progress Metrics */}
                    <div className="flex justify-around py-2">
                      <RadialProgress
                        value={stats.all}
                        max={stats.all}
                        label="Totali"
                        icon={Database}
                        color="#3b82f6"
                      />
                      <RadialProgress
                        value={hasFilters ? stats.total : stats.inView}
                        max={hasFilters ? stats.all : stats.total}
                        label={hasFilters ? "Filtrati" : "Visibili"}
                        icon={hasFilters ? Users : Globe}
                        color={hasFilters ? "#284899" : "#10b981"}
                      />
                      {hasFilters && (
                        <RadialProgress
                          value={stats.inView}
                          max={stats.total}
                          label="Visibili"
                          icon={Globe}
                          color="#10b981"
                        />
                      )}
                    </div>

                    {/* Donut Chart + Legend */}
                    <div className="p-4 rounded-xl bg-secondary/30 border border-border/50">
                      <div className="flex items-center gap-2 mb-4">
                        <Building2 className="h-4 w-4 text-[#284899]" />
                        <span className="text-[14px] font-semibold text-foreground">Distribuzione per categoria</span>
                      </div>
                      <div className="flex items-center gap-4">
                        <DonutChart items={stats.categories} />
                        <div className="flex-1 min-w-0 space-y-1.5">
                          {takeWithOthers(stats.categories, 5).map((cat, idx) => {
                            const IconComponent = cat.icon;
                            return (
                              <motion.div
                                key={idx}
                                className="flex items-center gap-1.5 min-w-0"
                                initial={{ opacity: 0, x: -10 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ delay: idx * 0.1 }}
                              >
                                <div
                                  className="w-2 h-2 rounded-full flex-shrink-0"
                                  style={{ backgroundColor: cat.color }}
                                />
                                {IconComponent && (
                                  <IconComponent className="h-3 w-3 flex-shrink-0" style={{ color: cat.color }} />
                                )}
                                <span className="text-[11px] text-muted-foreground truncate min-w-0 flex-1">{cat.label}</span>
                                <span className="text-[11px] font-semibold text-foreground tabular-nums flex-shrink-0 ml-1">
                                  {cat.percentage?.toFixed(1)}%
                                </span>
                              </motion.div>
                            );
                          })}
                        </div>
                      </div>
                    </div>

                  </>
                )}

                {/* Categories Tab */}
                {activeTab === "categories" && (
                  <>
                    {/* Categories Donut */}
                    <div className="p-4 rounded-xl bg-secondary/30 border border-border/50">
                      <div className="flex items-center gap-2 mb-4">
                        <Building2 className="h-4 w-4 text-blue-500" />
                        <span className="text-[14px] font-semibold text-foreground">Macro categorie</span>
                      </div>
                      <div className="flex justify-center mb-4">
                        <DonutChart items={stats.categories} size={160} />
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        {stats.categories.map((cat, idx) => {
                          const IconComponent = cat.icon;
                          return (
                            <motion.div
                              key={idx}
                              initial={{ opacity: 0, scale: 0.9 }}
                              animate={{ opacity: 1, scale: 1 }}
                              transition={{ delay: idx * 0.05 }}
                              className="flex items-center gap-2 p-2 rounded-lg bg-secondary/40"
                            >
                              {IconComponent && (
                                <IconComponent className="h-3.5 w-3.5 flex-shrink-0" style={{ color: cat.color }} />
                              )}
                              <div className="flex-1 min-w-0">
                                <p className="text-[12px] text-muted-foreground truncate">{cat.label}</p>
                                <p className="text-xs font-bold text-foreground tabular-nums">{cat.value.toLocaleString()}</p>
                              </div>
                            </motion.div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Typologies */}
                    <div className="p-4 rounded-xl bg-secondary/30 border border-border/50">
                      <div className="flex items-center gap-2 mb-4">
                        <Tag className="h-4 w-4 text-violet-500" />
                        <span className="text-[14px] font-semibold text-foreground">Tipologie</span>
                      </div>
                      <TypologyBars items={takeWithOthers(stats.typologies, 8)} />
                    </div>
                  </>
                )}

                {/* Regions Tab */}
                {activeTab === "regions" && (
                  <>
                    {/* Region Bars */}
                    <div className="p-4 rounded-xl bg-secondary/30 border border-border/50">
                      <div className="flex items-center gap-2 mb-4">
                        <MapPin className="h-4 w-4 text-emerald-500" />
                        <span className="text-[14px] font-semibold text-foreground">Distribuzione regionale</span>
                      </div>
                      <RegionBars items={stats.regions} />
                    </div>

                    {/* Top 3 Regions — exclude the "Others" bucket (it's an aggregate, not a region) */}
                    <div className="grid grid-cols-3 gap-2">
                      {stats.regions.filter(r => !r.isOthers).slice(0, 3).map((region, idx) => (
                        <motion.div
                          key={idx}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: idx * 0.1 }}
                          className={cn(
                            "p-3 rounded-xl text-center relative overflow-hidden",
                            idx === 0 && "bg-[#284899]/20 border-2 border-[#284899]/30",
                            idx === 1 && "bg-slate-400/20 border border-slate-400/30",
                            idx === 2 && "bg-[#284899]/20 border border-[#284899]/30"
                          )}
                        >
                          <div className={cn(
                            "absolute top-1 right-1 w-5 h-5 rounded-full flex items-center justify-center text-[12px] font-bold",
                            idx === 0 && "bg-[#284899] text-white",
                            idx === 1 && "bg-slate-400 text-white",
                            idx === 2 && "bg-[#284899] text-white"
                          )}>
                            {idx + 1}
                          </div>
                          <div className="text-sm font-bold text-foreground truncate mt-2" title={region.label}>
                            {region.label}
                          </div>
                          <div className="text-lg font-bold text-foreground tabular-nums">
                            {region.value.toLocaleString()}
                          </div>
                          <div className="text-[12px] text-muted-foreground tabular-nums">
                            {region.percentage?.toFixed(1)}%
                          </div>
                        </motion.div>
                      ))}
                    </div>
                  </>
                )}

                {/* Quality Tab */}
                {activeTab === "quality" && (
                  <>
                    {/* Star Ratings Visual */}
                    <div className="p-4 rounded-xl bg-secondary/30 border border-border/50">
                      <div className="flex items-center gap-2 mb-4">
                        <Star className="h-4 w-4 text-[#284899] fill-[#284899]" />
                        <span className="text-[14px] font-semibold text-foreground">Stelle</span>
                      </div>
                      <StarRatingVisual
                        distribution={stats.stars.distribution}
                        avgStars={stats.stars.avgStars}
                        withStars={stats.stars.withStars}
                      />
                    </div>

                    {/* Completeness Gauge */}
                    <div className="p-4 rounded-xl bg-secondary/30 border border-border/50">
                      <div className="flex items-center gap-2 mb-4">
                        <TrendingUp className="h-4 w-4 text-emerald-500" />
                        <span className="text-[14px] font-semibold text-foreground">Completezza dati</span>
                      </div>
                      <CompletenessGauge
                        data={[
                          { label: "Email", percentage: stats.completeness.email.pct },
                          { label: "Tel", percentage: stats.completeness.phone.pct },
                          { label: "Web", percentage: stats.completeness.website.pct },
                          { label: "Foto", percentage: stats.completeness.photo.pct },
                          { label: "Desc", percentage: stats.completeness.description.pct },
                        ]}
                      />
                    </div>

                    {/* Events Timeline */}
                    {stats.events.withDates > 0 && (
                      <div className="p-4 rounded-xl bg-secondary/30 border border-border/50">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4 text-blue-500" />
                            <span className="text-[14px] font-semibold text-foreground">Eventi temporali</span>
                          </div>
                          <span className="text-[12px] text-muted-foreground">
                            {stats.events.withDates} con date
                          </span>
                        </div>
                        <EventsTimeline events={stats.events} />
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Footer - only show when filters are active */}
              {hasFilters && (
                <div className="px-4 py-2 border-t border-border bg-secondary/20 flex items-center justify-center">
                  <span className="text-[12px] px-2 py-0.5 rounded-full bg-[#284899]/20 text-[#1e3570] font-medium">
                    Filtri attivi
                  </span>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
